import { ALL_NODES, MAIN_PATH, type NodeId } from "./graph";

export type NodeState = "past" | "current" | "active" | "future";

const MAIN_INDEX: Record<string, number> = Object.fromEntries(MAIN_PATH.map((n, i) => [n, i]));

export function resolveNodeState(node: NodeId, current: NodeId, active: Set<NodeId>): NodeState {
  if (node === current) return "current";
  if (active.has(node)) return "active";
  const mi = MAIN_INDEX[node];
  const ci = MAIN_INDEX[current];
  if (mi !== undefined && ci !== undefined && mi < ci) return "past";
  return "future";
}

const LABEL: Record<NodeId, { short: string; emoji: string }> = {
  mund: { short: "Mund", emoji: "👄" },
  speiseroehre: { short: "Spröhre", emoji: "〰️" },
  magen: { short: "Magen", emoji: "🫃" },
  leber: { short: "Leber", emoji: "🫀" },
  gallenblase: { short: "Gallenbl.", emoji: "🟢" },
  bauchspeicheldruese: { short: "Bauchsp.", emoji: "🫁" },
  zwoelffingerdarm: { short: "ZwFD", emoji: "🔀" },
  duenndarm: { short: "Dünndm", emoji: "🧬" },
  dickdarm: { short: "Dickdm", emoji: "🧻" },
  enddarm: { short: "Enddm", emoji: "💩" },
};

interface StepperHandle {
  root: SVGSVGElement;
  update(current: NodeId, active: NodeId[], bolusName: string): void;
}

// SVG layout (static for all 10 nodes + Brot/Stuhl terminals).
const POS: Record<NodeId, { x: number; y: number }> = {
  mund: { x: 95, y: 85 },
  speiseroehre: { x: 175, y: 85 },
  magen: { x: 260, y: 85 },
  leber: { x: 385, y: 28 },
  gallenblase: { x: 470, y: 28 },
  bauchspeicheldruese: { x: 470, y: 142 },
  zwoelffingerdarm: { x: 470, y: 85 },
  duenndarm: { x: 610, y: 85 },
  dickdarm: { x: 700, y: 85 },
  enddarm: { x: 790, y: 85 },
};

export function renderStepper(): StepperHandle {
  const NS = "http://www.w3.org/2000/svg";
  const root = document.createElementNS(NS, "svg");
  root.setAttribute("viewBox", "0 0 900 170");
  root.setAttribute("class", "stepper-svg");

  const edges: [NodeId | "start" | "end", NodeId | "start" | "end"][] = [
    ["start", "mund"],
    ["mund", "speiseroehre"],
    ["speiseroehre", "magen"],
    ["magen", "zwoelffingerdarm"],
    ["leber", "gallenblase"],
    ["gallenblase", "zwoelffingerdarm"],
    ["bauchspeicheldruese", "zwoelffingerdarm"],
    ["zwoelffingerdarm", "duenndarm"],
    ["duenndarm", "dickdarm"],
    ["dickdarm", "enddarm"],
    ["enddarm", "end"],
  ];
  const terminals = { start: { x: 24, y: 85 }, end: { x: 876, y: 85 } };
  for (const [a, b] of edges) {
    const pa = a === "start" ? terminals.start : a === "end" ? terminals.end : POS[a];
    const pb = b === "start" ? terminals.start : b === "end" ? terminals.end : POS[b];
    const line = document.createElementNS(NS, "line");
    line.setAttribute("x1", String(pa.x));
    line.setAttribute("y1", String(pa.y));
    line.setAttribute("x2", String(pb.x));
    line.setAttribute("y2", String(pb.y));
    line.setAttribute("class", "stepper-edge");
    if (a === "leber" || a === "gallenblase" || a === "bauchspeicheldruese") {
      line.classList.add("stepper-edge-gland");
    }
    root.appendChild(line);
  }

  const nodeGroups: Partial<Record<NodeId, SVGGElement>> = {};
  for (const id of ALL_NODES) {
    const g = document.createElementNS(NS, "g");
    g.setAttribute("class", "stepper-node");
    g.setAttribute("transform", `translate(${POS[id].x},${POS[id].y})`);
    const rect = document.createElementNS(NS, "rect");
    rect.setAttribute("x", "-33");
    rect.setAttribute("y", "-19");
    rect.setAttribute("width", "66");
    rect.setAttribute("height", "38");
    rect.setAttribute("rx", "8");
    g.appendChild(rect);
    const emoji = document.createElementNS(NS, "text");
    emoji.setAttribute("y", "-4");
    emoji.setAttribute("text-anchor", "middle");
    emoji.setAttribute("class", "stepper-emoji");
    emoji.textContent = LABEL[id].emoji;
    g.appendChild(emoji);
    const name = document.createElementNS(NS, "text");
    name.setAttribute("y", "12");
    name.setAttribute("text-anchor", "middle");
    name.setAttribute("class", "stepper-name");
    name.textContent = LABEL[id].short;
    g.appendChild(name);
    root.appendChild(g);
    nodeGroups[id] = g;
  }

  for (const [key, pos, emoji, label] of [
    ["start", terminals.start, "🍞", "Bolus"] as const,
    ["end", terminals.end, "💩", "Stuhl"] as const,
  ]) {
    const g = document.createElementNS(NS, "g");
    g.setAttribute("transform", `translate(${pos.x},${pos.y})`);
    g.setAttribute("class", "stepper-terminal");
    const txt = document.createElementNS(NS, "text");
    txt.setAttribute("text-anchor", "middle");
    txt.setAttribute("y", "-2");
    txt.setAttribute("class", "stepper-emoji");
    txt.textContent = emoji;
    g.appendChild(txt);
    const lbl = document.createElementNS(NS, "text");
    lbl.setAttribute("text-anchor", "middle");
    lbl.setAttribute("y", "14");
    lbl.setAttribute("class", "stepper-name");
    lbl.textContent = label;
    g.appendChild(lbl);
    root.appendChild(g);
    (nodeGroups as Record<string, SVGGElement>)[key] = g;
  }

  function update(current: NodeId, active: NodeId[], bolusName: string) {
    const activeSet = new Set(active);
    for (const id of ALL_NODES) {
      const g = nodeGroups[id];
      if (!g) continue;
      g.setAttribute("class", `stepper-node state-${resolveNodeState(id, current, activeSet)}`);
    }
    const startG = (nodeGroups as Record<string, SVGGElement>).start;
    if (startG) {
      const emojiNode = startG.querySelector(".stepper-emoji");
      if (emojiNode) emojiNode.textContent = bolusName.split(" ")[0] ?? "🍞";
    }
  }

  return { root, update };
}
